
import { Component, Prop, Vue } from "vue-property-decorator";
import UIkit from "uikit";
import FabricService from "@/services/fabric_service";
import FabricImageUpload from "@/components/FabricImageUpload.vue";
import BrandFields from "@/components/BrandFields.vue";
import FabricNameFields from "@/components/FabricNameFields.vue";
import FabricComparisonPicker from "@/components/FabricComparisonPicker.vue";
import {
  IFabric,
  missingRequiredFields,
  missingFieldKeys,
  isProtectedFabricCode,
  COLOR_GROUPS,
  COLOR_HEX,
  PATTERN_GROUPS,
  GRADES,
  FABRIC_TYPES,
  WEIGHT_OPTIONS,
} from "@/models/fabric";
import FabricStatusPicker from "@/components/FabricStatusPicker.vue";

@Component({
  components: {
    FabricImageUpload,
    BrandFields,
    FabricNameFields,
    FabricComparisonPicker,
    FabricStatusPicker,
  },
})
export default class EditView extends Vue {
  @Prop() private code!: string;

  private fabricService = new FabricService();
  protected fabric: IFabric | null = null;
  protected saving = false;

  protected retiredNames: string[] = [];
  protected colorGroupModel: string[] = [];
  protected patternGroupModel: string[] = [];

  // The full catalog, kept for the comparison pickers: they resolve stored
  // ObjectIds to fabrics and search across them. Served from FabricService's cache,
  // so this is the same fetch the dropdown options below already make.
  protected allFabrics: IFabric[] = [];

  // Data-driven dropdown options (distinct brand/mfr/collection values).
  protected brandOptions: string[] = [];
  protected mfrOptions: string[] = [];
  protected collectionOptions: string[] = [];

  protected colorGroups = COLOR_GROUPS;
  protected patternGroups = PATTERN_GROUPS;
  protected grades = GRADES;
  protected fabricTypes = FABRIC_TYPES;

  // Snapshot of the editable state as loaded from the DB, for dirty-checking.
  private pristine = "";

  // Set for the duration of the editor's own post-save URL rewrite, so
  // beforeRouteUpdate can tell that navigation apart from a user jumping to a
  // different fabric (which must reload the form, and prompt if it's dirty).
  private selfNavigating = false;

  // Which asset files actually exist on Wasabi. Drives (a) whether the swatch
  // upload's "also seed the 3D texture" box defaults on — it must not default to
  // overwriting a texture that already exists — and (b) whether "Copy Swatch
  // Image" is offered in the texture block. The fabric doc can't answer this:
  // texture_key is derived from fabric data and is present before any bytes are
  // ever written.
  protected assets: { swatch: boolean; texture: boolean } = {
    swatch: false,
    texture: false,
  };

  // Which asset blocks are holding staged (un-uploaded) changes. Asset edits used
  // to commit the instant you clicked them, which made them the one part of this
  // page that didn't obey the Save button. They now stage like every other field,
  // so they have to participate in the dirty check too.
  protected assetsPending: { swatch: boolean; texture: boolean } = {
    swatch: false,
    texture: false,
  };

  $refs!: {
    swatchUpload: FabricImageUpload;
    textureUpload: FabricImageUpload;
  };

  get mfrCode(): string {
    return this.code ?? (this.$route.query.code as string);
  }

  /** ?v= token for the asset previews — see assets_updated_at on the model. */
  get assetBust(): string {
    return this.fabric?.assets_updated_at || "";
  }

  protected get pendingAssetCount(): number {
    return Number(this.assetsPending.swatch) + Number(this.assetsPending.texture);
  }

  protected onAssetPendingChange(e: { kind: "swatch" | "texture"; pending: boolean }): void {
    this.assetsPending = { ...this.assetsPending, [e.kind]: e.pending };
  }

  /**
   * Fabrics the API will refuse to delete (WHP). Mirrors the server rule so the
   * UI doesn't offer an action that can only come back a 403 — the API is still
   * the thing enforcing it.
   */
  get isProtected(): boolean {
    return isProtectedFabricCode(this.fabric?.mfr_code);
  }

  // A soft-deleted fabric (deleted_at set) is shown read-only.
  get readOnly(): boolean {
    return !!(this.fabric && this.fabric.deleted_at);
  }

  async created(): Promise<void> {
    await Promise.all([this.loadFabric(), this.loadOptions()]);
  }

  mounted(): void {
    window.addEventListener("keydown", this.onKeydown);
  }

  beforeDestroy(): void {
    window.removeEventListener("keydown", this.onKeydown);
  }

  /**
   * Ctrl/Cmd+S saves, exactly as the Save button does.
   *
   * preventDefault runs BEFORE the guards, unconditionally: the shortcut has to
   * swallow the browser's "Save Page As…" dialog even when there is nothing for
   * us to save, or pressing it on a clean form dumps an .html file in Downloads.
   * The guards then mirror the button's own :disabled condition, so the keyboard
   * can never do something the button wouldn't — no saving a read-only fabric,
   * no double-submit mid-save, no empty PUT on an untouched form.
   *
   * v-model writes on `input`, so whatever field has focus is already committed
   * to `fabric` by the time this fires; there's nothing to flush first.
   */
  private onKeydown(e: KeyboardEvent): void {
    const key = e.key?.toLowerCase();
    if (key !== "s" || !(e.ctrlKey || e.metaKey) || e.altKey) return;
    e.preventDefault();
    if (this.saving || this.readOnly) return;
    // Silence is only acceptable when there is genuinely nothing to do. A save
    // the form is REFUSING has to say so — see notifyInvalid().
    if (!this.isValid) return this.notifyInvalid();
    if (!this.isDirty) return;
    this.save();
  }

  /**
   * Guard against walking away from unsaved work.
   *
   * This matters more than it used to: image edits are now staged rather than
   * uploaded on click, so a rotation or a picked file is just local state that
   * navigation would silently discard. Registered via Component.registerHooks in
   * main.ts — without that, this method never runs.
   */
  protected beforeRouteLeave(to: unknown, from: unknown, next: (ok?: boolean) => void): void {
    if (!this.isDirty || this.saving) return next();
    // eslint-disable-next-line no-alert
    next(window.confirm(this.discardPrompt));
  }

  /**
   * Edit → edit, same route with a different ?code= — which is what the header's
   * jump-to-fabric search does from this page.
   *
   * vue-router REUSES the component for that, so neither beforeRouteLeave nor
   * created() runs: without this hook the URL would change while the form kept
   * showing the previous fabric, and any unsaved work on it would be dropped with
   * no prompt. Registered alongside beforeRouteLeave in main.ts.
   */
  protected beforeRouteUpdate(
    to: { query: Record<string, string | undefined> },
    from: { query: Record<string, string | undefined> },
    next: (ok?: boolean) => void,
  ): void {
    // Our own post-save URL fix-up (see save()). The in-memory doc IS the fabric
    // at the new URL — reloading it would be a pointless refetch, and prompting
    // about "unsaved changes" right after a successful save would be a lie.
    if (this.selfNavigating) {
      this.selfNavigating = false;
      return next();
    }

    const sameFabric = to.query.code === from.query.code && to.query.group === from.query.group;
    if (sameFabric) return next();

    // eslint-disable-next-line no-alert
    if (this.isDirty && !this.saving && !window.confirm(this.discardPrompt)) {
      return next(false);
    }

    next();
    // Route params come from `to`, not from $route — at this point $route may not
    // have caught up with the navigation we just approved.
    this.reload(to.query.code, to.query.group);
  }

  private get discardPrompt(): string {
    const what = this.pendingAssetCount
      ? "You have unsaved changes, including image changes that have not been uploaded."
      : "You have unsaved changes.";
    return `${what}\n\nLeave this page and discard them?`;
  }

  /** Swap the editor onto a different fabric without a remount. */
  private async reload(code?: string, group?: string): Promise<void> {
    // Blanking the doc drops the form back to its spinner, so a slow fetch can't
    // leave the previous fabric on screen under the new fabric's URL.
    this.fabric = null;
    this.pristine = "";
    this.assets = { swatch: false, texture: false };
    this.assetsPending = { swatch: false, texture: false };
    await this.loadFabric(code, group);
  }

  private async loadFabric(
    code: string = this.mfrCode,
    group: string | undefined = this.$route.query.group as string | undefined,
  ): Promise<void> {
    if (!code) return;
    try {
      const doc = await this.fabricService.getByMfrCode(code, group);
      if (!doc) {
        UIkit.notification({ message: "Fabric not found.", status: "warning" });
        return;
      }
      // Ensure nested objects exist so v-model bindings are safe.
      if (!doc.name) doc.name = { color: "", display: "" };
      if (!doc.brand) doc.brand = { name: "", mfr: { name: "" } };
      if (!doc.brand.mfr) doc.brand.mfr = { name: "" };
      if (!doc.uses) doc.uses = { binding: false, fringe: false, underside: false };
      // Seed the comparison arrays BEFORE the doc becomes reactive. A fabric that
      // has never had comparisons set comes back from the aggregate without these
      // keys at all, and a property added to an already-reactive object in Vue 2
      // isn't reactive — the picker would emit and the UI would never update.
      if (!doc.comparisons) doc.comparisons = [];
      if (!doc.binding_comparisons) doc.binding_comparisons = [];
      if (!doc.fringe_comparisons) doc.fringe_comparisons = [];
      this.fabric = doc;
      this.retiredNames = [...(doc.retired_names || [])];
      this.colorGroupModel = [...(doc.color_group || [])];
      this.patternGroupModel = [...(doc.pattern_group || [])];
      // Capture the clean baseline AFTER the models are populated.
      this.$nextTick(() => {
        this.pristine = this.serialize();
      });
      // Fire-and-forget: the upload controls render immediately and simply
      // tighten their defaults once the probe lands.
      this.loadAssetStatus();
    } catch (err: any) {
      UIkit.notification({
        message: err?.response?.data?.message || "Could not load fabric.",
        status: "danger",
      });
    }
  }

  private async loadOptions(): Promise<void> {
    try {
      const all = await this.fabricService.list();
      this.allFabrics = all;
      const brands = new Set<string>();
      const mfrs = new Set<string>();
      const collections = new Set<string>();
      for (const f of all) {
        if (f.brand?.name) brands.add(f.brand.name);
        if (f.brand?.mfr?.name) mfrs.add(f.brand.mfr.name);
        if (f.brand?.brand_collection?.name) collections.add(f.brand.brand_collection.name);
      }
      this.brandOptions = Array.from(brands).sort();
      this.mfrOptions = Array.from(mfrs).sort();
      this.collectionOptions = Array.from(collections).sort();
    } catch (e) {
      // Non-fatal — dropdowns simply show whatever the fabric already has.
    }
  }

  // Canonical serialization of the editable state for dirty-checking.
  private serialize(): string {
    const f = this.fabric;
    if (!f) return "";
    return JSON.stringify({
      name: f.name,
      brand: f.brand,
      mfr_code: f.mfr_code,
      alt_mfr_code: f.alt_mfr_code || "",
      frankford_code: f.frankford_code || "",
      grade: f.grade,
      fabric_type: f.fabric_type || "",
      fabric_scale: f.fabric_scale,
      weight: f.weight || "",
      pantone: f.pantone || "",
      is_select: !!f.is_select,
      status: f.status || "",
      sort_order: f.sort_order ?? null,
      uses: f.uses,
      color_group: [...this.colorGroupModel].sort(),
      pattern_group: [...this.patternGroupModel].sort(),
      retired_names: this.retiredNames.map((n) => n.trim()).filter(Boolean),
      // Order is meaningful (it's the order staff added them), so these are NOT
      // sorted before comparison — reordering is a real edit.
      comparisons: [...(f.comparisons || [])],
      binding_comparisons: [...(f.binding_comparisons || [])],
      fringe_comparisons: [...(f.fringe_comparisons || [])],
    });
  }

  // Does the FORM differ from what was loaded from the DB? (Images are tracked
  // separately — they aren't part of the serialized snapshot.)
  get fieldsDirty(): boolean {
    if (!this.pristine) return false;
    return this.serialize() !== this.pristine;
  }

  // Save is enabled when anything on the page is unsaved — fields OR images.
  get isDirty(): boolean {
    return this.fieldsDirty || this.pendingAssetCount > 0;
  }

  /**
   * The API's own required fields. An existing fabric can be edited INTO an
   * invalid state (clear the color name, blank the scale), and the PUT would come
   * back as a Mongoose validation error the user can do nothing with — so the
   * same gate the add form uses applies here.
   */
  protected get missingFieldLabels(): string[] {
    return this.fabric ? missingRequiredFields(this.fabric).map((m) => m.label) : [];
  }

  /** Dotted keys of the missing fields — the form paints these inputs red. */
  protected get invalidFields(): Record<string, boolean> {
    return this.fabric ? missingFieldKeys(this.fabric) : {};
  }

  protected get isValid(): boolean {
    return this.missingFieldLabels.length === 0;
  }

  /**
   * Refuse the save out loud.
   *
   * This is the whole reason it exists: Ctrl+S can be pressed from anywhere on
   * the page, including well below the Save button and the red field. Greying out
   * a button the user can't see is not feedback — pressing a shortcut and having
   * nothing at all happen reads as a broken shortcut, not as a rejected save.
   */
  protected notifyInvalid(): void {
    UIkit.notification({
      message: `Can’t save yet — required: ${this.missingFieldLabels.join(", ")}.`,
      status: "danger",
      pos: "top-center",
      timeout: 6000,
    });
  }

  protected colorHex(color: string): string {
    return COLOR_HEX[color] || "#cccccc";
  }

  protected get weightOptions(): string[] {
    const opts = [...WEIGHT_OPTIONS];
    const w = this.fabric?.weight;
    if (w && !opts.includes(w)) opts.unshift(w); // preserve any legacy weight value
    return opts;
  }

  protected weightLabel(w: string): string {
    return WEIGHT_OPTIONS.includes(w) ? `${w} oz` : w;
  }

  protected formatDate(d: string): string {
    return new Date(d).toLocaleDateString();
  }

  private async loadAssetStatus(): Promise<void> {
    if (!this.fabric?._id) return;
    try {
      const { swatch, texture } = await this.fabricService.assetStatus(this.fabric._id);
      this.assets = { swatch, texture };
    } catch (e) {
      // Non-fatal. Leaving both false is the conservative fallback: the swatch
      // upload will offer to seed the texture (rather than silently skipping it
      // on a fabric that has none), and "Copy Swatch Image" stays disabled
      // rather than promising a copy we can't confirm is possible.
    }
  }

  /**
   * Flush both asset blocks, in whatever order they have pending work.
   *
   * MUST run before the metadata PUT. The Wasabi keys are derived from the
   * fabric's identity (brand / collection / name / mfr_code), so a PUT that
   * changes any of those makes the API rename the stored objects. Uploading first
   * writes to the key the objects currently live under and lets the rename carry
   * them across; uploading after would write to a key nothing points at.
   *
   * Throws on the first failure so save() can abort before touching metadata.
   */
  private async commitAssets(): Promise<void> {
    if (!this.fabric) return;

    for (const block of [this.$refs.swatchUpload, this.$refs.textureUpload]) {
      const res = await block?.commit();
      if (!res) continue;
      if (res.texture_key) this.fabric.texture_key = res.texture_key;
      if (res.kind === "swatch" && res.image) this.fabric.image = res.image;
      // The bytes just changed, so the CDN URL must too.
      if (res.cacheBust) this.fabric.assets_updated_at = String(res.cacheBust);
    }
  }

  protected async save(): Promise<void> {
    if (!this.fabric || this.readOnly) return;
    // Guards the paths the disabled button doesn't cover: Ctrl+S, and Enter
    // pressed inside a text input (which submits the form regardless).
    if (this.saving) return;
    if (!this.isValid) return this.notifyInvalid();
    this.saving = true;

    // This editor is addressed by ?code= (+ ?group= to disambiguate a repeated
    // mfr_code). Both are editable fields, so a save can invalidate the very URL
    // the user is sitting on — reloading it afterwards would 404. Capture what
    // the URL currently points at so we can re-point it once the save lands.
    const urlCode = this.mfrCode;
    const urlGroup = (this.$route.query.group as string) || undefined;

    // Images first, metadata second — the ordering is load-bearing; see
    // commitAssets(). If an upload fails we must NOT go on to save the fields:
    // the user asked for both, and half of it silently succeeding (with the
    // Save button then greying out) is how you lose an image edit without
    // noticing. Bail, keep everything staged, and say which part broke.
    // Read the count BEFORE committing: commit() clears each block's staged state,
    // and the resulting pending-change events land asynchronously.
    const assetsCommitted = this.pendingAssetCount > 0;
    try {
      await this.commitAssets();
      // The blocks have reset themselves; mirror that here rather than waiting on
      // their watchers, so isDirty is accurate the moment this returns.
      this.assetsPending = { swatch: false, texture: false };
    } catch (err: any) {
      this.saving = false;
      UIkit.notification({
        message:
          err?.response?.data?.message ||
          "There was a problem uploading the images. Nothing was saved.",
        status: "danger",
      });
      return;
    }

    try {
      // Mutate only edited fields on the full loaded doc so untouched data
      // (comparisons, specs, fringe/binding comparisons, etc.) is preserved.
      this.fabric.retired_names = this.retiredNames.filter((n) => n.trim() !== "");
      this.fabric.color_group = [...this.colorGroupModel];
      this.fabric.pattern_group = [...this.patternGroupModel];
      // Drop an empty brand_collection so we don't write a blank name.
      if (this.fabric.brand.brand_collection && !this.fabric.brand.brand_collection.name) {
        delete this.fabric.brand.brand_collection;
      }
      const updated = await this.fabricService.update(this.fabric);
      // Reflect any server-derived values (texture_key, image, coming_soon).
      // texture_key/image matter especially here: renaming the fabric changes the
      // derived filename, and the API renames the Wasabi objects to match, so the
      // previews must re-point at the new key or they'll 404 against the old one.
      if (updated && updated._id) {
        if (updated.texture_key) this.fabric.texture_key = updated.texture_key;
        if (updated.image !== undefined) this.fabric.image = updated.image;
      }
      // Re-baseline so the Save button disables again until the next change.
      this.pristine = this.serialize();

      const newCode = this.fabric.mfr_code;
      const newGroup = this.fabric.name.group || undefined;
      const identityChanged = newCode !== urlCode || newGroup !== urlGroup;

      if (identityChanged) {
        // `replace`, not `push`: the old URL is now dead (nothing resolves at
        // ?code=<oldCode>), so leaving it in history would hand the user a Back
        // button that lands on a "Fabric not found". Only the query changes, so
        // vue-router reuses this component — no remount, no refetch, no flicker;
        // the in-memory doc is already the saved one.
        const query: Record<string, string> = { code: newCode };
        if (newGroup) query.group = newGroup;
        this.selfNavigating = true;
        await this.$router
          .replace({ name: "edit", query })
          .catch(() => {
            // NavigationDuplicated is not an error here — but the guard never ran,
            // so clear the flag ourselves or it would swallow the user's NEXT jump.
            this.selfNavigating = false;
          });
      }

      // Images and the document are separate writes (Wasabi and Mongo can't share
      // a transaction), so say plainly when both happened.
      const imagePart = assetsCommitted ? " Images updated." : "";
      UIkit.notification({
        message: identityChanged
          ? `${this.fabric.name.display} saved.${imagePart} Code changed ${urlCode} → ${newCode}; assets and URL updated.`
          : `${this.fabric.name.display} (${newCode}) saved.${imagePart}`,
        status: "success",
      });

      // An upload can have created the very asset whose absence was driving the
      // defaults (the first swatch upload seeds a texture, after which a later
      // swatch re-upload must NOT default to overwriting it).
      if (assetsCommitted) await this.loadAssetStatus();
    } catch (err: any) {
      // The images are already on the CDN at this point — there is no rolling that
      // back, so don't imply otherwise. Being vague here would leave the user
      // retrying a "failed" save that already changed what customers see.
      const base =
        err?.response?.data?.message || "There was a problem saving this fabric.";
      UIkit.notification({
        message: assetsCommitted
          ? `The images were uploaded, but the fabric details failed to save. ${base}`
          : base,
        status: "danger",
      });
    } finally {
      this.saving = false;
    }
  }

  /**
   * Clone this fabric into the "add" form as a starting point. Copies the
   * standardized/reference data a sibling colorway shares (brand, grade, type,
   * weight, uses, pattern/color groups, specs) and deliberately drops everything
   * that must be unique or re-authored: identity codes, names, images, the
   * derived texture_key, comparisons, and lifecycle state.
   *
   * Handed over through the store rather than the query string — the payload is
   * a nested object well past what a URL should carry.
   */
  protected duplicate(): void {
    if (!this.fabric) return;
    const f = this.fabric;
    this.$store.commit("SET_FABRIC_DRAFT", {
      // Shared reference data — the whole point of duplicating.
      brand: JSON.parse(JSON.stringify(f.brand)),
      grade: f.grade,
      fabric_type: f.fabric_type,
      weight: f.weight,
      fabric_scale: f.fabric_scale,
      uses: { ...f.uses },
      color_group: [...(f.color_group || [])],
      pattern_group: [...(f.pattern_group || [])],
      specs: f.specs ? JSON.parse(JSON.stringify(f.specs)) : undefined,
      is_select: !!f.is_select,
      sort_order: undefined, // a clone must not fight the original for a slot
      // name.group is part of the collection's identity and is shared across a
      // colorway family, so it carries; color/display are per-fabric and do not.
      name: { group: f.name.group, color: "", display: "" },
    });
    this.$router.push({ name: "new", query: { from: f.mfr_code } });
  }

  protected async remove(): Promise<void> {
    if (!this.fabric || !this.fabric._id || this.readOnly) return;
    // The menu item is hidden for protected fabrics, but hiding a control is not
    // the same as disallowing the action — guard the call site too.
    if (this.isProtected) {
      UIkit.notification({
        message: `${this.fabric.mfr_code} is a protected fabric and cannot be deleted.`,
        status: "danger",
      });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${this.fabric.name.display} (${this.fabric.mfr_code})? This soft-deletes it (recoverable via the database).`)) return;
    try {
      await this.fabricService.remove(this.fabric._id);
      // Hand the message to the destination via the query string instead of
      // firing it here: this component is about to unmount, and the toast should
      // belong to the page the user actually lands on. App.vue renders it and
      // strips the param. (See App.vue#onQueryChange.)
      this.$router.push({
        name: "fabrics",
        query: {
          deleted: encodeURIComponent(`${this.fabric.name.display} (${this.fabric.mfr_code})`),
        },
      });
    } catch (err: any) {
      UIkit.notification({
        message: err?.response?.data?.message || "Could not delete fabric.",
        status: "danger",
      });
    }
  }
}
